/**
 * src/components/MoveDialog.tsx — the "Move to..." destination picker, shared by folders, documents
 * and preambles.
 *
 * Source: `D:/WCS/latex2html-french-trad/src/components/MoveDialog.tsx` (whole file, 222 lines).
 * Spec:   `docs/spec/06-frontend-components.md` §10.2, §10.3, §15 D-27/D-28, §16 ranks 7/8/17.
 *
 * Opened from three places: the sidebar's per-row `Move` button
 * (`EnhancedSavedDocuments.tsx:406-417`), the folder context menu's `Move to...`
 * (`FolderTree.tsx:483-495`), and `FolderTreeHandle.openMoveDialog`.
 *
 * ═══ DF-22 DEFECTS REPRODUCED HERE ════════════════════════════════════════════════════════════
 *
 *  - **rank 7 (`COALESCE` update semantics).** Choosing `Root` for a FOLDER sends
 *    `{ parent_id: null }`, which the Worker turns into `parent_id = COALESCE(NULL, parent_id)` —
 *    a no-op returning 200 (`worker/routes/folders.ts:301-313`). `onSuccess` below still toasts
 *    `'Folder moved successfully'` and closes the dialog, so the move *looks* like it worked and the
 *    folder has not moved. Documents and preambles are unaffected (their `folder_id` is
 *    present-vs-absent). Do NOT special-case `null` here.
 *
 *  - **rank 8's `TODO`, verbatim.** `handleMove` guards only `selectedTargetId === sourceId`; the
 *    descendant check is a comment. In practice this dialog cannot create a cycle anyway, because
 *    `renderFolder` returns `null` for the source node and descendants are rendered *inside* that
 *    subtree, so they are unreachable in the list (spec §10.2 — "MoveDialog is safe, drag-and-drop
 *    is not"). The `TODO` stays because removing it would overstate what the code checks.
 *
 *  - **rank 17 (cosmetic).** No `title` attribute on truncated names. Note `MoveDialog` DOES
 *    `truncate` folder names (`:172`) while `FolderTree` does not — the inconsistency is the
 *    source's and is preserved on both sides.
 *
 *  - The `Move` button is **disabled the moment the dialog opens**: the effect sets
 *    `selectedTargetId = currentParentId` and the button's `disabled` is
 *    `selectedTargetId === currentParentId` (`:51`, `:214`). That is by construction, not a bug to
 *    tidy away.
 *
 * `buildFolderTree` is duplicated between this file and `FolderTree.tsx` in the source. It is left
 * duplicated: the two are independently faithful copies, and hoisting them into a shared helper is a
 * refactor of code this port is not otherwise changing (CLAUDE.md § "Do not").
 */
import { useState, useEffect } from 'react'
import { useFolders, useUpdateFolder, useUpdateDocument, useUpdatePreamble } from '@/hooks/use-database'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Folder, FolderOpen, ChevronRight, ChevronDown, Home } from 'lucide-react'
import { toast } from 'sonner'
import type { Folder as FolderRow } from '@/lib/types'

/** Props — `MoveDialog.tsx:16-23`, field for field. */
interface MoveDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  sourceId: number
  sourceType: 'folder' | 'document' | 'preamble'
  /** For folders this is `parent_id`. For documents/preambles it is `folder_id`. (`:21`) */
  currentParentId: number | null
  onMoveSuccess?: () => void
}

/** `MoveDialog.tsx:25-30`. */
interface FolderNode {
  id: number
  name: string
  parent_id: number | null
  children: FolderNode[]
}

export const MoveDialog = ({
  open,
  onOpenChange,
  sourceId,
  sourceType,
  currentParentId,
  onMoveSuccess
}: MoveDialogProps) => {
  const { data: folders } = useFolders()
  const { mutate: updateFolder } = useUpdateFolder()
  const { mutate: updateDocument } = useUpdateDocument()
  const { mutate: updatePreamble } = useUpdatePreamble()

  const [selectedTargetId, setSelectedTargetId] = useState<number | null>(null)
  const [expandedFolders, setExpandedFolders] = useState<Set<number>>(new Set())

  // Reset selection when dialog opens — `:49-53`.
  useEffect(() => {
    if (open) {
      setSelectedTargetId(currentParentId)
    }
  }, [open, currentParentId])

  /** `:55-93`. */
  const handleMove = () => {
    if (selectedTargetId === currentParentId) {
      onOpenChange(false)
      return
    }

    // Prevent moving a folder into itself or its children
    if (sourceType === 'folder') {
      if (selectedTargetId === sourceId) {
        toast.error("Cannot move a folder into itself")
        return
      }
      // TODO: Check if target is a child of source (circular dependency)
      // For now, the backend or simple logic here can prevent direct self-move
      //
      // DF-22 rank 8 — this `TODO` is the source's (`MoveDialog.tsx:67-68`) and is reproduced
      // verbatim. Adding the ancestor walk here is the fix. Do not.
    }

    const onSuccess = () => {
      // DF-22 rank 7 — fires even when the Worker's `COALESCE(?, parent_id)` ignored a
      // `parent_id: null` move-to-Root. See the file header.
      toast.success(`${sourceType.charAt(0).toUpperCase() + sourceType.slice(1)} moved successfully`)
      onOpenChange(false)
      if (onMoveSuccess) onMoveSuccess()
    }

    if (sourceType === 'folder') {
      updateFolder({
        id: sourceId,
        updates: { parent_id: selectedTargetId }
      }, { onSuccess })
    } else if (sourceType === 'document') {
      updateDocument({
        id: sourceId,
        updates: { folder_id: selectedTargetId }
      }, { onSuccess })
    } else if (sourceType === 'preamble') {
      updatePreamble({
        id: sourceId,
        updates: { folder_id: selectedTargetId }
      }, { onSuccess })
    }
  }

  /**
   * Build folder tree structure — `:96-121`.
   *
   * ⚠️ **DF-22 rank 8 lives in the `if (parent)` guard, which has no `else`.** A node whose
   * `parent_id` is not in the map is silently dropped — so any folder caught in a drag-created
   * parent cycle disappears from this list too, exactly as it disappears from `FolderTree`. No
   * warning, no fallback to root. Reproduced, not repaired.
   */
  const buildFolderTree = (folders: FolderRow[]): FolderNode[] => {
    const folderMap = new Map<number, FolderNode>()
    const rootFolders: FolderNode[] = []

    folders?.forEach((folder) => {
      folderMap.set(folder.id, {
        id: folder.id,
        name: folder.name,
        parent_id: folder.parent_id,
        children: [],
      })
    })

    folderMap.forEach((folder) => {
      if (folder.parent_id === null) {
        rootFolders.push(folder)
      } else {
        const parent = folderMap.get(folder.parent_id)
        if (parent) {
          parent.children.push(folder)
        }
      }
    })

    return rootFolders
  }

  // `:123` — rebuilt on every render, deliberately not memoised (as in the source).
  const folderTree = buildFolderTree(folders || [])

  /** `:125-133`. Expansion state is local to the dialog and resets on unmount. */
  const toggleFolder = (folderId: number) => {
    const newExpanded = new Set(expandedFolders)
    if (newExpanded.has(folderId)) {
      newExpanded.delete(folderId)
    } else {
      newExpanded.add(folderId)
    }
    setExpandedFolders(newExpanded)
  }

  /** `:135-182`. Indent is `level * 16 + 8` px, matching `FolderTree` (`:148`). */
  const renderFolder = (folder: FolderNode, level: number = 0) => {
    const isExpanded = expandedFolders.has(folder.id)
    const isSelected = selectedTargetId === folder.id
    const isSource = sourceType === 'folder' && folder.id === sourceId

    // Disable moving into itself (visual feedback)
    //
    // Side effect the source relies on without saying so: because descendants are rendered INSIDE
    // this node, hiding the source hides its entire subtree — which is what actually prevents a
    // circular move through this dialog (spec §10.2).
    if (isSource) return null

    return (
      <div key={folder.id}>
        <div
          className={`flex items-center gap-2 px-2 py-1.5 rounded cursor-pointer hover:bg-accent transition-colors ${isSelected ? 'bg-accent text-accent-foreground' : ''
            }`}
          style={{ paddingLeft: `${level * 16 + 8}px` }}
          onClick={() => setSelectedTargetId(folder.id)}
        >
          {folder.children.length > 0 && (
            <button
              onClick={(e) => {
                e.stopPropagation()
                toggleFolder(folder.id)
              }}
              className="p-0 hover:bg-accent-foreground/10 rounded"
            >
              {isExpanded ? (
                <ChevronDown className="h-4 w-4" />
              ) : (
                <ChevronRight className="h-4 w-4" />
              )}
            </button>
          )}
          {folder.children.length === 0 && <div className="w-4" />}
          {isExpanded ? (
            <FolderOpen className="h-4 w-4 text-primary" />
          ) : (
            <Folder className="h-4 w-4 text-primary" />
          )}
          {/* DF-22 rank 17: `truncate` here but NOT in `FolderTree`, and no `title` tooltip in
              either. The asymmetry is the source's. */}
          <span className="text-sm flex-1 truncate">{folder.name}</span>
        </div>

        {isExpanded && folder.children.length > 0 && (
          <div>
            {folder.children.map((child) => renderFolder(child, level + 1))}
          </div>
        )}
      </div>
    )
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Move to...</DialogTitle>
          <DialogDescription>
            Select a destination folder.
          </DialogDescription>
        </DialogHeader>
        <div className="py-4">
          <ScrollArea className="h-[300px] border rounded-md p-2">
            <div className="space-y-1">
              {/* Root folder option */}
              <div
                className={`flex items-center gap-2 px-2 py-1.5 rounded cursor-pointer hover:bg-accent transition-colors ${selectedTargetId === null ? 'bg-accent text-accent-foreground' : ''
                  }`}
                onClick={() => setSelectedTargetId(null)}
              >
                <Home className="h-4 w-4 text-primary" />
                <span className="text-sm font-medium">Root</span>
              </div>

              {folderTree.map((folder) => renderFolder(folder))}
            </div>
          </ScrollArea>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          {/* Disabled on open by construction — see the file header. */}
          <Button onClick={handleMove} disabled={selectedTargetId === currentParentId}>
            Move
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
